"use client";

import { AnimatePresence, motion } from "motion/react";
import React from "react";
import { cn } from "@/lib/utils";
import type {
  BaseUncontrolledMotionHighlightProps,
  Bounds,
  MotionHighlightContextType,
  MotionHighlightProps,
  ParentModeMotionHighlightProps,
} from "../types";
import MotionHighlightItem from "./motion-highlight-item";

const DEFAULT_OFFSET = { top: 0, left: 0, width: 0, height: 0 } as const;

export const MotionHighlightContext = React.createContext<
  MotionHighlightContextType<any> | undefined
>(undefined);

export function useMotionHighlight<
  T extends string,
>(): MotionHighlightContextType<T> {
  const context = React.useContext(MotionHighlightContext);

  if (!context) {
    throw new Error(
      "useMotionHighlight must be used within a MotionHighlightProvider",
    );
  }

  return context as unknown as MotionHighlightContextType<T>;
}

function MotionHighlightRoot<T extends string>({
  value,
  defaultValue,
  onValueChange,
  controlledItems,
  transition = { type: "spring", stiffness: 350, damping: 35 },
  hover = false,
  enabled = true,
  disabled = false,
  exitDelay = 0.2,
  mode = "children",
  ref,
  className,
  children,
  ...props
}: MotionHighlightProps<T>) {
  const [activeValue, setActiveValue] = React.useState<T | null>(
    value ?? defaultValue ?? null,
  );
  const [boundsState, setBoundsState] = React.useState<Bounds | null>(null);
  const [activeClassNameState, setActiveClassNameState] =
    React.useState<string>("");
  const localRef = React.useRef<HTMLDivElement>(null);
  React.useImperativeHandle(ref, () => localRef.current as HTMLDivElement);

  // Normalize classNames
  const containerClassName =
    (props as ParentModeMotionHighlightProps)?.containerClassName ?? "";
  const itemsClassName =
    (props as BaseUncontrolledMotionHighlightProps)?.itemsClassName ?? "";

  // Normalize offset bounds
  const { boundsOffset } =
    (props as ParentModeMotionHighlightProps) ??
    ({} as ParentModeMotionHighlightProps);
  const offsetTop = boundsOffset?.top ?? DEFAULT_OFFSET.top;
  const offsetLeft = boundsOffset?.left ?? DEFAULT_OFFSET.left;
  const offsetWidth = boundsOffset?.width ?? DEFAULT_OFFSET.width;
  const offsetHeight = boundsOffset?.height ?? DEFAULT_OFFSET.height;

  const safeSetActiveValue = React.useCallback(
    (id: T | null) => {
      setActiveValue(prev => (prev === id ? prev : id));
      if (id !== activeValue) onValueChange?.(id as T);
    },
    [activeValue, onValueChange],
  );

  const safeSetBounds = React.useCallback(
    (bounds: DOMRect) => {
      if (!localRef.current) return;

      const containerRect = localRef.current.getBoundingClientRect();
      const newBounds: Bounds = {
        top: bounds.top - containerRect.top + offsetTop,
        left: bounds.left - containerRect.left + offsetLeft,
        width: bounds.width + offsetWidth,
        height: bounds.height + offsetHeight,
      };

      setBoundsState(prev => {
        if (
          prev &&
          prev.top === newBounds.top &&
          prev.left === newBounds.left &&
          prev.width === newBounds.width &&
          prev.height === newBounds.height
        ) {
          return prev;
        }
        return newBounds;
      });
    },
    [offsetTop, offsetLeft, offsetWidth, offsetHeight],
  );

  const clearBounds = React.useCallback(() => {
    setBoundsState(prev => (prev === null ? prev : null));
  }, []);

  React.useEffect(() => {
    if (value !== undefined) {
      setActiveValue(value);
    } else if (defaultValue !== undefined) {
      setActiveValue(defaultValue);
    }
  }, [value, defaultValue]);

  const id = React.useId();

  React.useEffect(() => {
    if (mode !== "parent") {
      return;
    }

    const container = localRef.current;

    if (!container) {
      return;
    }

    const onScroll = () => {
      if (!activeValue) {
        return;
      }

      const activeEl = container.querySelector<HTMLElement>(
        `[data-value="${activeValue}"][data-highlight="true"]`,
      );

      if (activeEl) {
        safeSetBounds(activeEl.getBoundingClientRect());
      }
    };

    container.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      container.removeEventListener("scroll", onScroll);
    };
  }, [mode, activeValue, safeSetBounds]);

  const render = React.useCallback(
    (children: React.ReactNode) => {
      if (mode === "parent") {
        return (
          <div
            ref={localRef}
            data-slot="motion-highlight-container"
            className={cn("relative", containerClassName)}
          >
            <AnimatePresence initial={false}>
              {boundsState && (
                <motion.div
                  data-slot="motion-highlight"
                  animate={{
                    top: boundsState.top,
                    left: boundsState.left,
                    width: boundsState.width,
                    height: boundsState.height,
                    opacity: 1,
                  }}
                  initial={{
                    top: boundsState.top,
                    left: boundsState.left,
                    width: boundsState.width,
                    height: boundsState.height,
                    opacity: 0,
                  }}
                  exit={{
                    opacity: 0,
                    transition: {
                      ...transition,
                      delay: (transition?.delay ?? 0) + (exitDelay ?? 0),
                    },
                  }}
                  transition={transition}
                  className={cn(
                    "absolute bg-muted z-0",
                    className,
                    activeClassNameState,
                  )}
                />
              )}
            </AnimatePresence>
            {children}
          </div>
        );
      }

      return children;
    },
    [
      mode,
      containerClassName,
      boundsState,
      transition,
      exitDelay,
      className,
      activeClassNameState,
    ],
  );

  return (
    <MotionHighlightContext.Provider
      value={{
        mode,
        activeValue,
        setActiveValue: safeSetActiveValue,
        id,
        hover,
        className,
        transition,
        disabled,
        enabled,
        exitDelay,
        setBounds: safeSetBounds,
        clearBounds,
        activeClassName: activeClassNameState,
        setActiveClassName: setActiveClassNameState,
        forceUpdateBounds: (props as ParentModeMotionHighlightProps)
          ?.forceUpdateBounds,
      }}
    >
      {enabled
        ? controlledItems
          ? render(children)
          : render(
              React.Children.map(children, (child, index) => (
                <MotionHighlightItem key={index} className={itemsClassName}>
                  {child}
                </MotionHighlightItem>
              )),
            )
        : children}
    </MotionHighlightContext.Provider>
  );
}

MotionHighlightRoot.displayName = "MotionHighlight";

export default MotionHighlightRoot;
